import brazilMap from "@svg-maps/brazil";
import { BRAZIL_UF_REGIONS, type BrazilRegion, type BrazilUf, isBrazilUf } from "./brazil-ufs";

/**
 * One geometry for every Brazil figure in the app: the login map, the
 * Coverage map, the loading territory and the regional card art.
 *
 * Geometry © @svg-maps/brazil contributors, CC BY 4.0. Paths are drawn in the
 * package's own 613 × 639 viewBox; nothing here carries fiscal meaning.
 */
export const BRAZIL_MAP_VIEWBOX = brazilMap.viewBox;
export const BRAZIL_MAP_WIDTH = 613;
export const BRAZIL_MAP_HEIGHT = 639;
export const BRAZIL_MAP_ATTRIBUTION_URL =
  "https://github.com/VictorCazanave/svg-maps/tree/master/packages/brazil";

export type BrazilUfShape = {
  uf: BrazilUf;
  region: BrazilRegion;
  path: string;
};

export type MapBounds = { x: number; y: number; width: number; height: number };

/** The 27 UF paths, in the package's drawing order (used for arrow-key order). */
export const BRAZIL_UF_SHAPES: readonly BrazilUfShape[] = brazilMap.locations.flatMap(
  (location: { id: string; path: string }) => {
    const uf = location.id.toUpperCase();
    return isBrazilUf(uf) ? [{ uf, region: BRAZIL_UF_REGIONS[uf], path: location.path }] : [];
  },
);

export const BRAZIL_UF_DRAW_ORDER: readonly BrazilUf[] = BRAZIL_UF_SHAPES.map((shape) => shape.uf);

const SHAPE_BY_UF = new Map(BRAZIL_UF_SHAPES.map((shape) => [shape.uf, shape]));

export function brazilUfShape(uf: string): BrazilUfShape | undefined {
  const code = uf.toUpperCase();
  return isBrazilUf(code) ? SHAPE_BY_UF.get(code) : undefined;
}

/**
 * Bounding boxes measured once from the package paths (SVG getBBox, rounded
 * outward). They frame the regional card art; they are not recomputed at
 * runtime because the geometry package is pinned.
 */
export const BRAZIL_REGION_BOUNDS: Record<BrazilRegion, MapBounds> = {
  north: { x: 0, y: 0, width: 441, height: 297 },
  northeast: { x: 394, y: 99, width: 219, height: 272 },
  centralWest: { x: 194, y: 196, width: 245, height: 271 },
  southeast: { x: 326, y: 305, width: 211, height: 183 },
  south: { x: 256, y: 441, width: 149, height: 198 },
};

/** São Paulo state, for the headquarters card art. */
export const SAO_PAULO_BOUNDS: MapBounds = { x: 326, y: 395, width: 140, height: 93 };

/**
 * Fits `bounds` into a `box` (both in user units) and returns the SVG
 * transform that draws package paths there, centred, aspect preserved.
 */
export function fitTransform(bounds: MapBounds, box: MapBounds): string {
  const scale = Math.min(box.width / bounds.width, box.height / bounds.height);
  const x = box.x + (box.width - bounds.width * scale) / 2 - bounds.x * scale;
  const y = box.y + (box.height - bounds.height * scale) / 2 - bounds.y * scale;
  return `translate(${round(x)} ${round(y)}) scale(${round(scale)})`;
}

function round(value: number): number {
  return Math.round(value * 1000) / 1000;
}
